import { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Users, Eye, TrendingUp, UserPlus, UserCheck, Globe2 } from 'lucide-react';
import { admin, type StatsPoint, type StatsRange, type VisitDetail } from '../../lib/api';

const RANGES: { key: StatsRange; label: string }[] = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

const SERIES = {
  impressions: { label: 'Impressions', color: '#2a78d6' },
  unique_visitors: { label: 'Unique visitors', color: '#1baf7a' },
};

const CHART_WIDTH = 720;
const CHART_HEIGHT = 280;
const PAD = { top: 16, right: 16, bottom: 32, left: 44 };

export default function AdminReport() {
  const [range, setRange] = useState<StatsRange>('daily');
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalImpressions, setTotalImpressions] = useState(0);
  const [newVisitors, setNewVisitors] = useState(0);
  const [returningVisitors, setReturningVisitors] = useState(0);
  const [data, setData] = useState<StatsPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const [visits, setVisits] = useState<VisitDetail[]>([]);
  const [visitsTotal, setVisitsTotal] = useState(0);
  const [visitsPage, setVisitsPage] = useState(1);
  const [visitsLoading, setVisitsLoading] = useState(true);

  const load = useCallback(async (r: StatsRange) => {
    setLoading(true);
    try {
      const res = await admin.stats(r);
      setTotalUsers(res.totalUsers);
      setTotalImpressions(res.totalImpressions);
      setNewVisitors(res.newVisitors);
      setReturningVisitors(res.returningVisitors);
      setData(res.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVisits = useCallback(async (page: number) => {
    setVisitsLoading(true);
    try {
      const res = await admin.visits(page);
      setVisits(res.visits);
      setVisitsTotal(res.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load visit log');
    } finally {
      setVisitsLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [range, load]);
  useEffect(() => { loadVisits(visitsPage); }, [visitsPage, loadVisits]);

  const rangeUniqueTotal = useMemo(
    () => data.reduce((sum, d) => sum + d.unique_visitors, 0),
    [data]
  );

  const innerW = CHART_WIDTH - PAD.left - PAD.right;
  const innerH = CHART_HEIGHT - PAD.top - PAD.bottom;
  const maxValue = Math.max(1, ...data.map(d => d.impressions));

  const x = (i: number) => data.length <= 1 ? PAD.left : PAD.left + (i / (data.length - 1)) * innerW;
  const y = (v: number) => PAD.top + innerH - (v / maxValue) * innerH;

  const linePath = (key: 'impressions' | 'unique_visitors') =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d[key])}`).join(' ');

  const yTicks = 4;
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((maxValue / yTicks) * i));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" /> Site Report
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Registered users and traffic overview</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{totalUsers.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Registered users</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Eye className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{totalImpressions.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total impressions (all time)</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f7f0' }}>
            <TrendingUp className="w-5 h-5" style={{ color: SERIES.unique_visitors.color }} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{rangeUniqueTotal.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Unique visitors in selected range</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{newVisitors.toLocaleString()}</p>
            <p className="text-sm text-gray-500">New visitors in selected range</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{returningVisitors.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Returning visitors in selected range</p>
          </div>
        </div>
      </div>

      {/* Range toggle */}
      <div className="flex gap-2">
        {RANGES.map(r => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              range === r.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No traffic recorded yet for this range</div>
        ) : (
          <>
            {/* Legend */}
            <div className="flex items-center gap-5 mb-4 text-sm">
              {(Object.keys(SERIES) as (keyof typeof SERIES)[]).map(key => (
                <div key={key} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: SERIES[key].color }} />
                  <span className="text-gray-600">{SERIES[key].label}</span>
                </div>
              ))}
            </div>

            <svg
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              className="w-full h-auto"
              onMouseLeave={() => setHoverIndex(null)}
            >
              {/* Gridlines + y labels */}
              {yTickValues.map((v, i) => (
                <g key={i}>
                  <line x1={PAD.left} x2={CHART_WIDTH - PAD.right} y1={y(v)} y2={y(v)} stroke="#e1e0d9" strokeWidth={1} />
                  <text x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="#898781">
                    {v}
                  </text>
                </g>
              ))}
              {/* Baseline */}
              <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={CHART_HEIGHT - PAD.bottom} stroke="#c3c2b7" strokeWidth={1} />
              <line x1={PAD.left} x2={CHART_WIDTH - PAD.right} y1={CHART_HEIGHT - PAD.bottom} y2={CHART_HEIGHT - PAD.bottom} stroke="#c3c2b7" strokeWidth={1} />

              {/* X labels (sparse) */}
              {data.map((d, i) => {
                const step = Math.ceil(data.length / 8);
                if (i % step !== 0 && i !== data.length - 1) return null;
                return (
                  <text key={i} x={x(i)} y={CHART_HEIGHT - PAD.bottom + 18} textAnchor="middle" fontSize={10} fill="#898781">
                    {d.period.slice(5) || d.period}
                  </text>
                );
              })}

              {/* Lines */}
              <path d={linePath('impressions')} fill="none" stroke={SERIES.impressions.color} strokeWidth={2} strokeLinecap="round" />
              <path d={linePath('unique_visitors')} fill="none" stroke={SERIES.unique_visitors.color} strokeWidth={2} strokeLinecap="round" />

              {/* Hover targets */}
              {data.map((d, i) => (
                <g key={i}>
                  <rect
                    x={x(i) - (innerW / Math.max(1, data.length - 1)) / 2}
                    y={PAD.top}
                    width={innerW / Math.max(1, data.length - 1)}
                    height={innerH}
                    fill="transparent"
                    onMouseEnter={() => setHoverIndex(i)}
                  />
                  {hoverIndex === i && (
                    <>
                      <line x1={x(i)} x2={x(i)} y1={PAD.top} y2={CHART_HEIGHT - PAD.bottom} stroke="#c3c2b7" strokeWidth={1} strokeDasharray="3,3" />
                      <circle cx={x(i)} cy={y(d.impressions)} r={4} fill={SERIES.impressions.color} stroke="#fff" strokeWidth={2} />
                      <circle cx={x(i)} cy={y(d.unique_visitors)} r={4} fill={SERIES.unique_visitors.color} stroke="#fff" strokeWidth={2} />
                    </>
                  )}
                </g>
              ))}
            </svg>

            {hoverIndex !== null && data[hoverIndex] && (
              <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 inline-block">
                <span className="font-semibold">{data[hoverIndex].period}</span>
                {' — '}
                <span style={{ color: SERIES.impressions.color }}>Impressions: {data[hoverIndex].impressions}</span>
                {'  '}
                <span style={{ color: SERIES.unique_visitors.color }}>Unique: {data[hoverIndex].unique_visitors}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Accessible data table */}
      {data.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Impressions</th>
                  <th className="px-4 py-2 font-medium">Unique visitors</th>
                  <th className="px-4 py-2 font-medium">New</th>
                  <th className="px-4 py-2 font-medium">Returning</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map(d => (
                  <tr key={d.period}>
                    <td className="px-4 py-2 text-gray-900">{d.period}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-700">{d.impressions}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-700">{d.unique_visitors}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-700">{d.new_visitors}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-700">{d.returning_visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed visit log */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Globe2 className="w-5 h-5 text-blue-600" /> Recent Visits
        </h2>
        <div className="card overflow-hidden p-0">
          {visitsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : visits.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No visits recorded yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="px-4 py-2 font-medium">Date/Time</th>
                    <th className="px-4 py-2 font-medium">User</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Location</th>
                    <th className="px-4 py-2 font-medium">IP address</th>
                    <th className="px-4 py-2 font-medium">Device</th>
                    <th className="px-4 py-2 font-medium">Browser</th>
                    <th className="px-4 py-2 font-medium">OS</th>
                    <th className="px-4 py-2 font-medium">Path</th>
                    <th className="px-4 py-2 font-medium">Referrer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visits.map(v => {
                    const location = [v.city, v.region, v.country].filter(Boolean).join(', ') || '—';
                    return (
                      <tr key={v.id}>
                        <td className="px-4 py-2 text-gray-700">{new Date(v.created_at * 1000).toLocaleString()}</td>
                        <td className="px-4 py-2 text-gray-900">{v.user_full_name || v.user_email || 'Anonymous'}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v.is_returning ? 'bg-violet-100 text-violet-700' : 'bg-green-100 text-green-700'}`}>
                            {v.is_returning ? 'Returning' : 'New'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{location}</td>
                        <td className="px-4 py-2 text-gray-500 font-mono text-xs">{v.ip_address || '—'}</td>
                        <td className="px-4 py-2 text-gray-700 capitalize">{v.device_type}</td>
                        <td className="px-4 py-2 text-gray-700">{v.browser || '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{v.os || '—'}</td>
                        <td className="px-4 py-2 text-gray-500">{v.path}</td>
                        <td className="px-4 py-2 text-gray-500 max-w-[160px] truncate">{v.referrer || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {visitsTotal > 25 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={() => setVisitsPage(p => Math.max(1, p - 1))}
              disabled={visitsPage === 1}
              className="btn-secondary px-3 py-1.5 text-sm"
            >
              Prev
            </button>
            <span className="flex items-center text-sm text-gray-500">
              Page {visitsPage} of {Math.ceil(visitsTotal / 25)}
            </span>
            <button
              onClick={() => setVisitsPage(p => Math.min(Math.ceil(visitsTotal / 25), p + 1))}
              disabled={visitsPage >= Math.ceil(visitsTotal / 25)}
              className="btn-secondary px-3 py-1.5 text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
